import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {IssueItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {MilestoneIcon} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'

import {RepoIssuesBulkPickerAnchor, type RepoIssuesBulkActionProps} from './RepoIssuesBulkActionShared'

export function milestoneAppliedToAll(issues: ReadonlyArray<Pick<IssueItem, 'milestone'>>) {
  const milestone = issues[0]?.milestone
  if (!milestone?.id || !issues.every(issue => issue.milestone?.id === milestone.id)) return null

  return {
    id: milestone.id,
    title: milestone.title,
    closed: false,
    closedAt: null,
    dueOn: null,
    progressPercentage: 0,
    url: milestone.url,
  } satisfies MilestonePickerMilestone
}

export function RepoIssuesBulkMilestone({
  activeMilestone,
  disabled,
  nested,
  owner,
  repo,
  onUpdate,
}: RepoIssuesBulkActionProps & {activeMilestone: MilestonePickerMilestone | null}) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => (
      <RepoIssuesBulkPickerAnchor {...{anchorProps, disabled, nested}} icon={MilestoneIcon} label="Milestone" />
    ),
    [disabled, nested],
  )
  const noMilestoneItem = useMemo<ExtendedItemProps<MilestonePickerMilestone>>(
    () => ({
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      children: SPECIAL_VALUES.noMilestoneData.title,
      source: {
        id: SPECIAL_VALUES.noMilestoneData.id,
        title: SPECIAL_VALUES.noMilestoneData.title,
        closed: false,
        closedAt: null,
        dueOn: null,
        progressPercentage: 0,
        url: '',
      },
      groupId: '',
      leadingVisual: () => <MilestoneIcon />,
    }),
    [],
  )

  return (
    <MilestonePicker
      anchorElement={anchorElement}
      title="Apply milestone"
      repo={repo}
      owner={owner}
      readonly={disabled}
      activeMilestone={activeMilestone}
      noMilestoneItem={noMilestoneItem}
      onSelectionChange={milestones => {
        const milestone = milestones[0]
        const clearMilestone = !milestone || milestone.id === SPECIAL_VALUES.noMilestoneData.id
        onUpdate(clearMilestone ? {clearMilestone: true} : {milestoneId: milestone.id})
      }}
      canCreateMilestone={false}
      nested={nested}
    />
  )
}
